import { z } from 'zod';
import type { IsoDate } from './common.js';
import { dueDateSchema } from './tasks.js';

/** Planned sprints wait in the backlog; only one sprint of a team is active at a time. */
export const sprintStatuses = ['planned', 'active', 'completed'] as const;
export type SprintStatus = (typeof sprintStatuses)[number];

export const sprintStatusLabels: Record<SprintStatus, string> = {
  planned: 'Planificado',
  active: 'Activo',
  completed: 'Completado',
};

const datesInOrder = (s: { startDate?: string | null; endDate?: string | null }) =>
  !s.startDate || !s.endDate || s.startDate <= s.endDate;
const datesMessage = { message: 'El fin no puede ser antes del inicio', path: ['endDate'] };

const reviewSchema = z.string().trim().max(5000).nullable();

const sprintFields = z.object({
  name: z.string().trim().min(1, 'Obligatorio').max(80),
  goal: z.string().trim().max(500).nullable(),
  startDate: dueDateSchema.nullable(),
  endDate: dueDateSchema.nullable(),
  review: reviewSchema,
});

export const createSprintSchema = sprintFields
  .omit({ review: true })
  .partial({ goal: true, startDate: true, endDate: true })
  .refine(datesInOrder, datesMessage);
export type CreateSprintInput = z.input<typeof createSprintSchema>;

export const updateSprintSchema = sprintFields.partial().refine(datesInOrder, datesMessage);
export type UpdateSprintInput = z.infer<typeof updateSprintSchema>;

export const startSprintSchema = z
  .object({ startDate: dueDateSchema, endDate: dueDateSchema })
  .refine(datesInOrder, datesMessage);
export type StartSprintInput = z.infer<typeof startSprintSchema>;

export const completeSprintSchema = z.object({
  /** Where unfinished tasks go: the backlog or a planned sprint. */
  moveTo: z.union([z.literal('backlog'), z.uuid()]).default('backlog'),
  /** Retrospective: what went well, what to improve. */
  review: reviewSchema.optional(),
});
export type CompleteSprintInput = z.input<typeof completeSprintSchema>;

export interface Sprint {
  id: string;
  teamId: string;
  name: string;
  goal: string | null;
  status: SprintStatus;
  startDate: string | null;
  endDate: string | null;
  completedAt: IsoDate | null;
  createdAt: IsoDate;
  /** What the team committed to when it started (null until then). */
  committed: { points: number; tasks: number } | null;
  /** What got done, and how many tasks moved on, when it was completed. */
  result: { points: number; tasks: number; carriedOver: number } | null;
  review: string | null;
}
